import { useContext } from 'react'

import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box,
  Button, Form, Image, Link, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useAgencySelector from '@hooks/useAgencySelector'
import useSavedArtwork, { useSavedArtworkForm } from '@hooks/useSavedArtwork'

import EntitySelectBox from '@components/EntitySelectBox'
import { confirmDelete } from '@functions/entity'

const MODAL_KEY = 'ManageSavedArtworkModal'

type ModalCallbacks = {
  closeModal: () => void,
  deleteSavedArtwork: (payload: HandleCallbackActionParams) => void,
  updateSavedArtwork: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageSavedArtworkModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateSavedArtwork } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { savedArtwork } = digObject(modalData, MODAL_KEY, {})

  const { hasMultipleAgencies } = useAgencySelector()

  const savedArtworkFormPayload = useSavedArtworkForm(savedArtwork, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      agency_id,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = savedArtworkFormPayload

  const {
    callbacks: { updateSavedArtwork: updateFn, deleteSavedArtwork: deleteFn },
    deleting,
    firstSavedArtworkPage,
    updating,
  } = useSavedArtwork(savedArtwork)

  const { previews } = firstSavedArtworkPage

  const updateSavedArtworkPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Saved Artwork Updated',
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    options: {
      entityParams: savedArtwork,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Saved Artwork',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Saved Artwork" />

      <SidebarModal.Content>
        <Form.Field direction="column" label="Preview">
          <Box variant="whiteGrey">
            <Link href={previews?.original} target="_blank" textProps={{ width: '100%', height: '100%' }}>
              <Image
                backgroundSize="contain"
                borderRadius={5}
                forwardProps={{ position: 'relative' }}
                height={240}
                // @ts-expect-error TS2322
                placeHolder
                url={previews?.thumb}
              />
            </Link>
          </Box>
        </Form.Field>

        <Form.Field label="Title" errorMessage={errors.title} marginTop="large">
          <input value={title} name="title" {...handlers} />
        </Form.Field>

        {hasMultipleAgencies && (
          <Form.Field errorMessage={errors.agency_id} label="Agency" marginTop="large">
            <EntitySelectBox
              data-value-type="number"
              entityKey="agencies"
              // @ts-expect-error TS2322
              name="agency_id"
              value={agency_id}
              {...handlers}
            />
          </Form.Field>
        )}

        <Form.Field label="More Options" marginTop="large">
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            loading={deleting}
            onClick={() => confirmDelete(confirmDeletePayload)}
            size="medium"
          >
            Delete Saved Artwork
          </Button>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateSavedArtwork(updateSavedArtworkPayload)}
          size="large"
        >
          {saveEnabled ? 'Save Saved Artwork' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageSavedArtworkModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
